import React, { useState } from 'react';
import './ScientistNote.css';

/**
 * Shared "note paper" instruction card used by every puzzle, in every room (algebra,
 * chemistry, map, etc). Collapses down to a single floating icon button (not just a
 * shorter header bar) so it can never sit on top of a puzzle's own controls/scene, even
 * while collapsed - this behavior lives here once instead of being reimplemented (or
 * skipped) per puzzle. The "Scientist Notes" framing is just the default flavor (labs,
 * workshops) - a room with a different theme (e.g. the map room's "Explorer's Notes") can
 * override `title` without duplicating this component.
 *
 * @param {Array} lines - Static hint/instruction lines (from getPuzzleNoteLines)
 * @param {React.ReactNode} children - Optional extra content appended after the
 *   static lines (e.g. a live readout), hidden along with the rest when collapsed
 * @param {string} title - Header text, defaults to the original "📝 Scientist Notes" -
 *   its leading emoji doubles as the collapsed icon button's glyph
 * @param {boolean} defaultCollapsed - Start collapsed (as just the icon button) instead of
 *   expanded - for a puzzle whose own live view/controls are the main event and shouldn't be
 *   upstaged by a wall of text on first render. Every existing caller omits this (stays
 *   expanded-by-default, unchanged).
 * @param {string} language - Optional 'ta' | 'en'. When passed, `lines` is filtered down to
 *   just the lines written in that language (detected per-line by the presence of Tamil
 *   script) before rendering - for a room (currently only tamil-cave) whose note lines are
 *   written as whole Tamil sentences followed by whole English ones, each its own array
 *   entry, and that wants a single master toggle to show only one language at a time.
 *   Omitted entirely by every other caller, which keeps showing every line exactly as before.
 * @param {boolean} collapsed - Optional controlled collapse state. When passed (non-null),
 *   this component stops owning its own collapsed/expanded state and stops rendering its own
 *   floating icon button while collapsed (it renders nothing at all in that case) - the
 *   caller is expected to render that entry point itself elsewhere (see GameHeader.jsx's
 *   `onToggleNotes` icon, merged into the same icon column instead of floating as its own
 *   separate circle) and flip this prop. Omitted by every caller that hasn't opted into that,
 *   which keeps the fully self-contained icon-button behavior unchanged.
 * @param {Function} onToggleCollapsed - Required alongside `collapsed` - called (with no
 *   args) whenever this component would otherwise have flipped its own state, i.e. clicking
 *   the header to close it.
 */
function ScientistNote({
  lines = [],
  children,
  title = '📝 Scientist Notes',
  defaultCollapsed = false,
  language = null,
  collapsed: collapsedProp = null,
  onToggleCollapsed = null
}) {
  const [collapsedState, setCollapsedState] = useState(defaultCollapsed);
  const isControlled = collapsedProp !== null;
  const collapsed = isControlled ? collapsedProp : collapsedState;
  const toggle = () => (isControlled ? onToggleCollapsed?.() : setCollapsedState((prev) => !prev));

  // Leading emoji of the title doubles as the collapsed icon's glyph, falling back to the
  // default note icon for any title that happens not to start with one.
  const iconMatch = title.match(/^(\p{Emoji_Presentation}|\p{Extended_Pictographic})️?/u);
  const icon = iconMatch ? iconMatch[0] : '📝';

  const visibleLines = language
    ? lines.filter((line) => {
        if (typeof line !== 'string') return true;
        const isTamil = /[஀-௿]/.test(line);
        return language === 'en' ? !isTamil : isTamil;
      })
    : lines;

  if (collapsed) {
    if (isControlled) return null;
    return (
      <button
        type="button"
        className="scientist-note-icon"
        onClick={() => setCollapsedState(false)}
        aria-label={`Open ${title}`}
        title={title}
      >
        <span className="scientist-note-icon-symbol">{icon}</span>
      </button>
    );
  }

  return (
    <div className="scientist-note-paper">
      <div className="scientist-note-header" onClick={toggle}>
        <span>{title}</span>
        <span className="scientist-note-collapse-arrow" aria-label="Collapse notes" title="Collapse notes">▾</span>
      </div>
      {visibleLines.map((line, idx) => (
        <p key={`scientist-line-${idx}`} className="scientist-note-line" style={{ marginTop: idx === 0 ? '8px' : '6px' }}>
          {line}
        </p>
      ))}
      {children}
    </div>
  );
}

export default ScientistNote;
